import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

function IEPSCalculator({ theme }) {
    const { t } = useLanguage();
    const [iepsData, setIepsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeFilter, setTimeFilter] = useState('month');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [expandedProducts, setExpandedProducts] = useState(new Set());

    // Initialize month/year to current
    useEffect(() => {
        const now = new Date();
        setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
        setSelectedYear(String(now.getFullYear()));
    }, []);

    const fetchIepsData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let url = `${BACKEND_URL}/api/admin/ieps-report?period=${timeFilter}`;
            if (timeFilter === 'month' && selectedMonth) {
                url += `&month=${selectedMonth}`;
            } else if (timeFilter === 'year' && selectedYear) {
                url += `&year=${selectedYear}`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error(t('failedToFetchIepsData'));

            const data = await response.json();
            setIepsData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [timeFilter, selectedMonth, selectedYear, t]);

    useEffect(() => {
        if ((timeFilter === 'month' && selectedMonth) ||
            (timeFilter === 'year' && selectedYear) ||
            timeFilter === 'all') {
            fetchIepsData();
        }
    }, [timeFilter, selectedMonth, selectedYear, fetchIepsData]);

    const toggleProductExpansion = (productId) => {
        const newExpanded = new Set(expandedProducts);
        if (newExpanded.has(productId)) {
            newExpanded.delete(productId);
        } else {
            newExpanded.add(productId);
        }
        setExpandedProducts(newExpanded);
    };

    const navigateMonth = (direction) => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const date = new Date(year, month - 1, 1);
        date.setMonth(date.getMonth() + direction);
        const newMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        setSelectedMonth(newMonth);
    };

    const navigateYear = (direction) => {
        const newYear = String(parseInt(selectedYear) + direction);
        setSelectedYear(newYear);
    };

    const calculateTotals = () => {
        return iepsData.reduce((acc, product) => {
            acc.totalSales += parseFloat(product.total_sales_value || 0);
            acc.totalLiters += parseFloat(product.total_liters_sold || 0);
            acc.totalUnits += parseInt(product.total_units_sold || 0);
            return acc;
        }, { totalSales: 0, totalLiters: 0, totalUnits: 0 });
    };

    const exportToCSV = async () => {
        if (!iepsData || iepsData.length === 0) return;

        try {
            // Fetch enhanced data for CSV export
            let url = `${BACKEND_URL}/api/admin/ieps-report-enhanced?period=${timeFilter}`;
            if (timeFilter === 'month' && selectedMonth) {
                url += `&month=${selectedMonth}`;
            } else if (timeFilter === 'year' && selectedYear) {
                url += `&year=${selectedYear}`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error(t('failedToFetchIepsData'));
            const data = await response.json();

            // Helper to escape CSV values
            const escapeCSV = (value) => {
                if (value === null || value === undefined) return '';
                const stringValue = String(value);
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            };

            // Build CSV content with three sections
            const csvSections = [];

            // SECTION 1: Beer Products - Selected Period
            const periodLabel = timeFilter === 'month' ? selectedMonth :
                              timeFilter === 'year' ? selectedYear :
                              t('allTime');

            csvSections.push(`"${t('beerSales')} - ${periodLabel}"`);
            csvSections.push([
                t('productName'),
                t('unitsSold'),
                t('litersSold'),
                t('salesValue'),
                t('averagePricePerLiter')
            ].join(','));

            data.section1.forEach(product => {
                const avgPrice = product.total_liters_sold > 0
                    ? (product.total_sales_value / product.total_liters_sold).toFixed(2)
                    : '0.00';

                csvSections.push([
                    escapeCSV(product.product_name),
                    product.total_units_sold,
                    parseFloat(product.total_liters_sold).toFixed(2),
                    parseFloat(product.total_sales_value).toFixed(2),
                    avgPrice
                ].join(','));
            });

            // SECTION 2: Beer Production - All production up to end of selected period
            csvSections.push(''); // Blank line separator
            csvSections.push(''); // Extra blank line
            const productionLabel = timeFilter === 'month'
                ? `${t('beerProduction')} - ${t('allTime')} ${t('upTo')} ${selectedMonth}`
                : timeFilter === 'year'
                ? `${t('beerProduction')} - ${t('allTime')} ${t('upTo')} ${selectedYear}`
                : `${t('beerProduction')} - ${t('allTime')}`;

            csvSections.push(`"${productionLabel}"`);
            csvSections.push([
                'Beer Name',
                'Start Date',
                'End Date',
                'Quantity',
                'Units',
                'Insumos'
            ].join(','));

            if (data.section2 && data.section2.length > 0) {
                data.section2.forEach(item => {
                    const startDate = item.start_date
                        ? new Date(item.start_date).toLocaleDateString()
                        : 'N/A';
                    const endDate = item.finish_date
                        ? new Date(item.finish_date).toLocaleDateString()
                        : 'N/A';

                    csvSections.push([
                        escapeCSV(item.product_name),
                        startDate,
                        endDate,
                        parseFloat(item.production_quantity).toFixed(2),
                        item.production_units,
                        escapeCSV(item.insumos_list || 'N/A')
                    ].join(','));
                });
            } else {
                csvSections.push('No production data');
            }

            // SECTION 3: Beer Products - All-time cumulative up to selected period
            csvSections.push(''); // Blank line separator
            csvSections.push(''); // Extra blank line
            const section3Label = timeFilter === 'month'
                ? `${t('beerSales')} - ${t('allTime')} ${t('upTo')} ${selectedMonth}`
                : timeFilter === 'year'
                ? `${t('beerSales')} - ${t('allTime')} ${t('upTo')} ${selectedYear}`
                : `${t('beerSales')} - ${t('allTime')}`;
            csvSections.push(`"${section3Label}"`);
            csvSections.push([
                t('productName'),
                t('unitsSold'),
                t('litersSold'),
                t('salesValue'),
                t('averagePricePerLiter')
            ].join(','));

            data.section3.forEach(product => {
                const avgPrice = product.total_liters_sold > 0
                    ? (product.total_sales_value / product.total_liters_sold).toFixed(2)
                    : '0.00';

                csvSections.push([
                    escapeCSV(product.product_name),
                    product.total_units_sold,
                    parseFloat(product.total_liters_sold).toFixed(2),
                    parseFloat(product.total_sales_value).toFixed(2),
                    avgPrice
                ].join(','));
            });

            // SECTION 4: All Products by Month
            csvSections.push(''); // Blank line separator
            csvSections.push(''); // Extra blank line
            csvSections.push(`"${t('allProducts')} - ${periodLabel}"`);
            csvSections.push([
                t('productName'),
                t('category'),
                t('salesValue')
            ].join(','));

            data.section4.forEach(item => {
                csvSections.push([
                    escapeCSV(item.product_name),
                    escapeCSV(item.category_name),
                    parseFloat(item.sales_value).toFixed(2)
                ].join(','));
            });

            // Create and download CSV
            const csvContent = csvSections.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const downloadUrl = URL.createObjectURL(blob);

            const filename = `ieps-report-enhanced-${timeFilter}-${selectedMonth || selectedYear || 'all'}.csv`;
            link.setAttribute('href', downloadUrl);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error exporting CSV:', err);
            setError(t('error') + ': ' + err.message);
        }
    };

    const totals = calculateTotals();
    const avgPricePerLiter = totals.totalLiters > 0
        ? (totals.totalSales / totals.totalLiters).toFixed(2)
        : '0.00';

    if (loading) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: theme.colors.textSecondary
            }}>
                {t('loadingIepsData')}...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: theme.colors.error || '#d32f2f'
            }}>
                {t('error')}: {error}
            </div>
        );
    }

    return (
        <div style={{ padding: '0rem' }}>
            {/* Filter Controls */}
            <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => setTimeFilter('month')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timeFilter === 'month' ? theme.colors.primary : theme.colors.surface,
                            color: timeFilter === 'month' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timeFilter === 'month' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byMonth')}
                    </button>
                    <button
                        onClick={() => setTimeFilter('year')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timeFilter === 'year' ? theme.colors.primary : theme.colors.surface,
                            color: timeFilter === 'year' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timeFilter === 'year' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byYear')}
                    </button>
                    <button
                        onClick={() => setTimeFilter('all')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timeFilter === 'all' ? theme.colors.primary : theme.colors.surface,
                            color: timeFilter === 'all' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timeFilter === 'all' ? 'bold' : 'normal'
                        }}
                    >
                        {t('allTime')}
                    </button>
                </div>

                {timeFilter === 'month' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateMonth(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface
                            }}
                        />
                        <button
                            onClick={() => navigateMonth(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                {timeFilter === 'year' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateYear(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="number"
                            min="2020"
                            max="2099"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            placeholder={t('selectYear')}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface,
                                width: '120px'
                            }}
                        />
                        <button
                            onClick={() => navigateYear(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                <button
                    onClick={exportToCSV}
                    disabled={!iepsData || iepsData.length === 0}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme.colors.secondary || '#666',
                        color: theme.colors.surface,
                        border: 'none',
                        borderRadius: theme.borderRadius || 4,
                        cursor: iepsData.length > 0 ? 'pointer' : 'not-allowed',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        opacity: iepsData.length > 0 ? 1 : 0.5
                    }}
                >
                    📊 {t('exportReport')}
                </button>
            </div>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
            }}>
                <div style={{
                    padding: '1rem',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`,
                    boxShadow: theme.boxShadow
                }}>
                    <div style={{
                        fontSize: '0.8rem',
                        color: theme.colors.textSecondary,
                        marginBottom: '0.5rem'
                    }}>
                        {t('totalSalesValue')}
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: theme.colors.primary
                    }}>
                        ${totals.totalSales.toFixed(2)}
                    </div>
                </div>

                <div style={{
                    padding: '1rem',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`,
                    boxShadow: theme.boxShadow
                }}>
                    <div style={{
                        fontSize: '0.8rem',
                        color: theme.colors.textSecondary,
                        marginBottom: '0.5rem'
                    }}>
                        {t('totalLitersSold')}
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: theme.colors.primary
                    }}>
                        {totals.totalLiters.toFixed(2)} L
                    </div>
                </div>

                <div style={{
                    padding: '1rem',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`,
                    boxShadow: theme.boxShadow
                }}>
                    <div style={{
                        fontSize: '0.8rem',
                        color: theme.colors.textSecondary,
                        marginBottom: '0.5rem'
                    }}>
                        {t('totalServings')}
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: theme.colors.primary
                    }}>
                        {totals.totalUnits}
                    </div>
                </div>

                <div style={{
                    padding: '1rem',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`,
                    boxShadow: theme.boxShadow
                }}>
                    <div style={{
                        fontSize: '0.8rem',
                        color: theme.colors.textSecondary,
                        marginBottom: '0.5rem'
                    }}>
                        {t('averagePricePerLiter')}
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: theme.colors.primary
                    }}>
                        ${avgPricePerLiter}/L
                    </div>
                </div>
            </div>

            {/* Data Table */}
            {iepsData.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: theme.colors.textSecondary,
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`
                }}>
                    {t('noSalesData')}
                </div>
            ) : (
                <div style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius || 8,
                    border: `1px solid ${theme.colors.border}`,
                    overflow: 'hidden'
                }}>
                    {/* Table Header */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 50px',
                        gap: '1rem',
                        padding: '1rem',
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.surface,
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                    }}>
                        <div>{t('productName')}</div>
                        <div style={{ textAlign: 'right' }}>{t('unitsSold')}</div>
                        <div style={{ textAlign: 'right' }}>{t('litersSold')}</div>
                        <div style={{ textAlign: 'right' }}>{t('salesValue')}</div>
                        <div style={{ textAlign: 'right' }}>$/L</div>
                        <div></div>
                    </div>

                    {/* Table Body */}
                    {iepsData.map((product) => {
                        const isExpanded = expandedProducts.has(product.product_id);
                        const avgPrice = product.total_liters_sold > 0
                            ? (product.total_sales_value / product.total_liters_sold).toFixed(2)
                            : '0.00';

                        return (
                            <div key={product.product_id}>
                                {/* Product Row */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 50px',
                                        gap: '1rem',
                                        padding: '1rem',
                                        borderBottom: `1px solid ${theme.colors.border}`,
                                        backgroundColor: theme.colors.background,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onClick={() => toggleProductExpansion(product.product_id)}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.surface}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.background}
                                >
                                    <div style={{
                                        color: theme.colors.text,
                                        fontWeight: '500'
                                    }}>
                                        {product.product_name}
                                    </div>
                                    <div style={{
                                        textAlign: 'right',
                                        color: theme.colors.text
                                    }}>
                                        {product.total_units_sold}
                                    </div>
                                    <div style={{
                                        textAlign: 'right',
                                        color: theme.colors.text
                                    }}>
                                        {parseFloat(product.total_liters_sold).toFixed(2)}
                                    </div>
                                    <div style={{
                                        textAlign: 'right',
                                        color: theme.colors.text
                                    }}>
                                        ${parseFloat(product.total_sales_value).toFixed(2)}
                                    </div>
                                    <div style={{
                                        textAlign: 'right',
                                        color: theme.colors.text
                                    }}>
                                        ${avgPrice}
                                    </div>
                                    <div style={{
                                        textAlign: 'center',
                                        color: theme.colors.primary,
                                        fontSize: '1.2rem'
                                    }}>
                                        {isExpanded ? '▼' : '▶'}
                                    </div>
                                </div>

                                {/* Breakdown Rows */}
                                {isExpanded && product.breakdown && product.breakdown.length > 0 && (
                                    <div style={{
                                        backgroundColor: theme.colors.surface,
                                        borderBottom: `1px solid ${theme.colors.border}`,
                                        padding: '0.5rem 1rem 1rem 2rem'
                                    }}>
                                        <div style={{
                                            fontSize: '0.85rem',
                                            fontWeight: 'bold',
                                            color: theme.colors.textSecondary,
                                            marginBottom: '0.5rem'
                                        }}>
                                            {t('pourSizeBreakdown')}:
                                        </div>
                                        {product.breakdown.map((option, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 50px',
                                                    gap: '1rem',
                                                    padding: '0.5rem 0',
                                                    fontSize: '0.85rem',
                                                    color: theme.colors.textSecondary
                                                }}
                                            >
                                                <div style={{ paddingLeft: '1rem' }}>
                                                    └─ {option.option_name}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {option.units_sold}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {parseFloat(option.liters_sold).toFixed(2)}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    ${parseFloat(option.sales_value).toFixed(2)}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {option.liters_sold > 0
                                                        ? `$${(option.sales_value / option.liters_sold).toFixed(2)}`
                                                        : '-'
                                                    }
                                                </div>
                                                <div></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default IEPSCalculator;
